import {
	mentionFromPayload,
	type PromptSegment,
	parsePromptSegments,
	serializeAccountMention,
	serializeVideoMention,
} from "./promptMentionSerialization";
import type { QuestionBoxVideoMentionData } from "./QuestionBoxVideoMention";

/**
 * The QuestionBox document model.
 *
 * This is the source of truth for the Markdown-enabled editor: a tree of typed
 * containers rather than a raw string. A {@link QuestionDocument} is a list of
 * {@link Block}s (one visual line each); each block has a {@link BlockKind}
 * (paragraph / heading / quote / list item) plus an ordered list of content
 * {@link Inline} nodes.
 *
 * Block markers (`#`, `>`, `-`, `1.`) are **structural metadata** on the block —
 * they are *not* stored in the content and never appear in the DOM or the caret
 * offset stream. The view renders them purely as decoration (a bullet, a number,
 * heading styling), and the serializers re-emit them. This keeps the document
 * and view genuinely separate: the caret lives in the DOM, and the DOM contains
 * only content, so mapping DOM<->model is a trivial 1:1 character count.
 *
 * Inline emphasis (`**`, `_`, `` ` ``, `~~`) is different: it stays as raw
 * characters inside `text` nodes and is decorated only in the view (so submitted
 * Markdown is byte-identical and incomplete tokens stay literal).
 *
 * Atoms (pills / account mentions) are first-class nodes with explicit
 * boundaries — no sentinel characters, so arbitrary user text can never collide
 * with the model.
 *
 * All operations here are pure: they take a document and return a new one with
 * an updated {@link DocSelection}, and never touch the DOM.
 *
 * Offsets are a single linear "content offset": each UTF-16 code unit of text
 * counts 1, each atom counts 1, and the newline between two blocks counts 1.
 * Block markers count 0 (they are not content).
 */

/** Account-mention (`@handle`) data, mirroring the chip's dataset attributes. */
export interface DocMentionData {
	type: string;
	handle: string;
	platform?: string;
	id?: string;
	/**
	 * The account's avatar, shown in the chip. Presentational rather than part of
	 * the mention's identity: it is known when the mention came from a search hit
	 * or a context row, and absent when it was pasted or read back from a prompt
	 * (the serialised forms carry identity only), so the chip falls back to a
	 * glyph.
	 */
	avatarUrl?: string | null;
}

export function questionBoxAccountMentionKey(
	data: Pick<DocMentionData, "type" | "handle" | "platform">,
): string {
	return `${data.type}:${data.platform ?? ""}:${data.handle.toLowerCase()}`;
}

export type Inline =
	| { kind: "text"; value: string }
	| { kind: "pill"; data: QuestionBoxVideoMentionData }
	| { kind: "mention"; data: DocMentionData };

/** A block's structural type; its marker is derived from this, not from content. */
export type BlockKind =
	| { type: "paragraph" }
	| { type: "heading"; level: number }
	| { type: "quote" }
	| { type: "list-ul"; bullet: string }
	| { type: "list-ol"; number: number; delim: string };

export interface Block {
	kind: BlockKind;
	inlines: Inline[];
}

export interface DocSelection {
	anchor: number;
	focus: number;
}

export interface QuestionDocument {
	blocks: Block[];
	selection: DocSelection;
}

const PARAGRAPH: BlockKind = { type: "paragraph" };

/** Partial `@handle` immediately before the caret (a mention being typed). */
const mentionAtCaretRegex =
	/(?<=^|\W)@([^\s,;:!?"'()[\]{}<>/\\|#$%^&*+=~`]{0,50})$/;

/**
 * The `@` query while the context selector is open. Spaces and the punctuation
 * that would otherwise end a mention stay inside it, because a title is a
 * phrase. A line break still ends it.
 */
const mentionPhraseAtCaretRegex = /(?<=^|\W)@([^\n\r\u2028\u2029]{0,50})$/;

/** Every `@handle` in a run of text. A handle has no spaces, unlike the phrase above. */
const handleScanRegex =
	/(?<=^|\W)@([^\s,;:!?"'()[\]{}<>/\\|#$%^&*+=~`]{1,50})/g;

// --- Block markers --------------------------------------------------------

/** The raw Markdown marker (with trailing space) a block kind serialises to. */
function markerFor(kind: BlockKind): string {
	switch (kind.type) {
		case "heading":
			return `${"#".repeat(kind.level)} `;
		case "quote":
			return "> ";
		case "list-ul":
			return `${kind.bullet} `;
		case "list-ol":
			return `${kind.number}${kind.delim} `;
		default:
			return "";
	}
}

/** Parses a leading block marker from raw line text into a kind + remaining text. */
function parseBlockMarker(line: string): { kind: BlockKind; rest: string } {
	let m = /^(#{1,6}) /.exec(line);
	if (m) {
		return {
			kind: { type: "heading", level: m[1].length },
			rest: line.slice(m[0].length),
		};
	}
	if (line.startsWith("> "))
		return { kind: { type: "quote" }, rest: line.slice(2) };
	m = /^([-*+]) /.exec(line);
	if (m)
		return { kind: { type: "list-ul", bullet: m[1] }, rest: line.slice(2) };
	m = /^(\d{1,9})([.)]) /.exec(line);
	if (m) {
		return {
			kind: { type: "list-ol", number: Number(m[1]), delim: m[2] },
			rest: line.slice(m[0].length),
		};
	}
	return { kind: PARAGRAPH, rest: line };
}

const isContinuable = (kind: BlockKind): boolean =>
	kind.type === "list-ul" || kind.type === "list-ol" || kind.type === "quote";

/** The kind a continued line takes when Enter is pressed in a block of `kind`. */
function continuationKind(kind: BlockKind): BlockKind {
	switch (kind.type) {
		case "list-ul":
			return { type: "list-ul", bullet: kind.bullet };
		case "list-ol":
			return { type: "list-ol", number: kind.number + 1, delim: kind.delim };
		case "quote":
			return { type: "quote" };
		default:
			// Headings are single-line; everything else continues as a paragraph.
			return PARAGRAPH;
	}
}

// --- Measurement ----------------------------------------------------------

const inlineLength = (inline: Inline): number =>
	inline.kind === "text" ? inline.value.length : 1;

const blockLength = (block: Block): number =>
	block.inlines.reduce((total, inline) => total + inlineLength(inline), 0);

/** Total addressable offset: all block content plus one newline between blocks. */
export function docLength(doc: QuestionDocument): number {
	const content = doc.blocks.reduce(
		(total, block) => total + blockLength(block),
		0,
	);
	return content + Math.max(0, doc.blocks.length - 1);
}

const clamp = (offset: number, doc: QuestionDocument): number =>
	Math.max(0, Math.min(offset, docLength(doc)));

const collapsed = (offset: number): DocSelection => ({
	anchor: offset,
	focus: offset,
});

/** `[from, to]` with `from <= to`. */
export function selectionBounds(selection: DocSelection): [number, number] {
	return selection.anchor <= selection.focus
		? [selection.anchor, selection.focus]
		: [selection.focus, selection.anchor];
}

/** Resolves an offset to a `{ block, inner }` position; a block end wins over the next block start. */
function locate(
	doc: QuestionDocument,
	offset: number,
): { block: number; inner: number } {
	let acc = 0;
	for (let b = 0; b < doc.blocks.length; b++) {
		const len = blockLength(doc.blocks[b]);
		if (offset <= acc + len) return { block: b, inner: offset - acc };
		acc += len + 1; // block content + the newline that follows it
	}
	const last = doc.blocks.length - 1;
	return { block: last, inner: blockLength(doc.blocks[last]) };
}

// --- Inline/block helpers -------------------------------------------------

/** Merges adjacent text runs and drops empty ones so inline lists stay canonical. */
function normalizeInlines(inlines: Inline[]): Inline[] {
	const out: Inline[] = [];
	for (const inline of inlines) {
		if (inline.kind === "text") {
			if (inline.value.length === 0) continue;
			const last = out[out.length - 1];
			if (last?.kind === "text") {
				out[out.length - 1] = {
					kind: "text",
					value: last.value + inline.value,
				};
				continue;
			}
		}
		out.push(inline);
	}
	return out;
}

/** Splits a block's content inlines at `inner`; offsets never fall inside an atom. */
function splitInlines(
	block: Block,
	inner: number,
): { before: Inline[]; after: Inline[] } {
	const before: Inline[] = [];
	const after: Inline[] = [];
	let acc = 0;
	for (const inline of block.inlines) {
		const len = inlineLength(inline);
		if (acc + len <= inner) {
			before.push(inline);
		} else if (acc >= inner) {
			after.push(inline);
		} else if (inline.kind === "text") {
			const cut = inner - acc;
			before.push({ kind: "text", value: inline.value.slice(0, cut) });
			after.push({ kind: "text", value: inline.value.slice(cut) });
		}
		acc += len;
	}
	return { before, after };
}

const blocksContentLength = (blocks: Block[]): number =>
	blocks.reduce((total, block) => total + blockLength(block), 0) +
	Math.max(0, blocks.length - 1);

/**
 * Renumbers ordered lists so each contiguous run of `list-ol` blocks counts
 * `1, 2, 3, …`. Every run always starts at 1 (a list split in two therefore
 * restarts, rather than the tail keeping its old numbers), and a run is broken
 * by any non-list block or a change of delimiter. Ordered-list markers are
 * structural metadata that count 0 toward content offsets, so this never shifts
 * the selection — callers apply it as a final normalisation after any
 * block-structure change (insert, delete, split, outdent).
 */
function renumberOrderedLists(blocks: Block[]): Block[] {
	let number = 0;
	let runDelim = "";
	return blocks.map((block) => {
		const kind = block.kind;
		if (kind.type !== "list-ol") {
			number = 0;
			return block;
		}
		number = number > 0 && kind.delim === runDelim ? number + 1 : 1;
		runDelim = kind.delim;
		return number === kind.number
			? block
			: { ...block, kind: { ...kind, number } };
	});
}

// --- Core splice ----------------------------------------------------------

/**
 * Replaces the content between offsets `from` and `to` with `insert` (an array
 * of blocks, so it may introduce block breaks). The block the range starts in
 * keeps its kind (typing never changes a block's kind — only the input rules
 * and newline op do); interior/trailing inserted blocks keep their own kinds.
 * Returns a new document with the caret collapsed at the end of the insert.
 */
function spliceBlocks(
	doc: QuestionDocument,
	from: number,
	to: number,
	insert: Block[],
): QuestionDocument {
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	const a = locate(doc, lo);
	const b = locate(doc, hi);
	const startKind = doc.blocks[a.block].kind;
	const left = splitInlines(doc.blocks[a.block], a.inner).before;
	const right = splitInlines(doc.blocks[b.block], b.inner).after;

	const insertBlocks =
		insert.length > 0 ? insert : [{ kind: PARAGRAPH, inlines: [] }];
	let newBlocks: Block[];
	if (insertBlocks.length === 1) {
		const merged: Block = {
			kind: startKind,
			inlines: normalizeInlines([
				...left,
				...insertBlocks[0].inlines,
				...right,
			]),
		};
		newBlocks = [
			...doc.blocks.slice(0, a.block),
			merged,
			...doc.blocks.slice(b.block + 1),
		];
	} else {
		const first: Block = {
			kind: startKind,
			inlines: normalizeInlines([...left, ...insertBlocks[0].inlines]),
		};
		const middle = insertBlocks.slice(1, -1);
		const lastIns = insertBlocks[insertBlocks.length - 1];
		const last: Block = {
			kind: lastIns.kind,
			inlines: normalizeInlines([...lastIns.inlines, ...right]),
		};
		newBlocks = [
			...doc.blocks.slice(0, a.block),
			first,
			...middle,
			last,
			...doc.blocks.slice(b.block + 1),
		];
	}

	const caret = lo + blocksContentLength(insertBlocks);
	const next: QuestionDocument = {
		blocks: renumberOrderedLists(newBlocks),
		selection: collapsed(caret),
	};
	return { ...next, selection: collapsed(clamp(caret, next)) };
}

const textToBlocks = (text: string): Block[] =>
	text.split("\n").map((line) => ({
		kind: PARAGRAPH,
		inlines: line.length > 0 ? [{ kind: "text", value: line } as Inline] : [],
	}));

// --- Public operations ----------------------------------------------------

/** Replaces the current selection with plain text (may contain newlines). */
export function insertText(
	doc: QuestionDocument,
	text: string,
): QuestionDocument {
	const [from, to] = selectionBounds(doc.selection);
	return spliceBlocks(doc, from, to, textToBlocks(text));
}

/** Replaces the current selection with the given atom/inline nodes (one line). */
export function insertInlines(
	doc: QuestionDocument,
	inlines: Inline[],
): QuestionDocument {
	const [from, to] = selectionBounds(doc.selection);
	return spliceBlocks(doc, from, to, [{ kind: PARAGRAPH, inlines }]);
}

/** Replaces `[from, to]` with `inlines` (single line, start block kind kept). */
export function replaceRange(
	doc: QuestionDocument,
	from: number,
	to: number,
	inlines: Inline[],
): QuestionDocument {
	return spliceBlocks(doc, from, to, [{ kind: PARAGRAPH, inlines }]);
}

/** Deletes the content between `from` and `to`, merging blocks across a break. */
export function deleteRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): QuestionDocument {
	return spliceBlocks(doc, from, to, [{ kind: PARAGRAPH, inlines: [] }]);
}

/** Deletes the current selection, or one unit backward when collapsed. */
export function deleteBackward(doc: QuestionDocument): QuestionDocument {
	const [from, to] = selectionBounds(doc.selection);
	if (from !== to) return deleteRange(doc, from, to);
	if (from === 0) return doc;
	return deleteRange(doc, from - 1, from);
}

/** Deletes the current selection, or one unit forward when collapsed. */
export function deleteForward(doc: QuestionDocument): QuestionDocument {
	const [from, to] = selectionBounds(doc.selection);
	if (from !== to) return deleteRange(doc, from, to);
	if (to >= docLength(doc)) return doc;
	return deleteRange(doc, to, to + 1);
}

/**
 * Pastes text at the current selection, interpreting Markdown block markers so
 * copy/paste is symmetric with {@link toSourceText}. Each pasted line's leading
 * marker becomes a structural block kind *when that line starts a line in the
 * result*: interior and trailing pasted lines always do; the first pasted line
 * does only when the caret sits at the start of its block (otherwise its marker
 * is re-inserted as literal text so nothing is lost). Inline mention JSON still
 * becomes atoms.
 */
export function insertPaste(
	doc: QuestionDocument,
	text: string,
): QuestionDocument {
	const [from, to] = selectionBounds(doc.selection);
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	const a = locate(doc, lo);
	const b = locate(doc, hi);
	const left = splitInlines(doc.blocks[a.block], a.inner).before;
	const right = splitInlines(doc.blocks[b.block], b.inner).after;
	const startKind = doc.blocks[a.block].kind;

	const pasted = segmentsToLines(text).map(extractLeadingMarker);
	const atLineStart = a.inner === 0;

	// The first pasted line only keeps its own kind at a genuine line start;
	// otherwise the marker is restored as literal text and the block's kind wins.
	const firstMarker = atLineStart ? "" : markerFor(pasted[0].kind);
	const firstInlines = firstMarker
		? [{ kind: "text" as const, value: firstMarker }, ...pasted[0].inlines]
		: pasted[0].inlines;
	const firstKind = atLineStart ? pasted[0].kind : startKind;

	let newBlocks: Block[];
	if (pasted.length === 1) {
		newBlocks = [
			{
				kind: firstKind,
				inlines: normalizeInlines([...left, ...firstInlines, ...right]),
			},
		];
	} else {
		const last = pasted[pasted.length - 1];
		newBlocks = [
			{
				kind: firstKind,
				inlines: normalizeInlines([...left, ...firstInlines]),
			},
			...pasted.slice(1, -1),
			{
				kind: last.kind,
				inlines: normalizeInlines([...last.inlines, ...right]),
			},
		];
	}

	const blocks = renumberOrderedLists([
		...doc.blocks.slice(0, a.block),
		...newBlocks,
		...doc.blocks.slice(b.block + 1),
	]);
	const caret = lo + blocksContentLength(pasted) + firstMarker.length;
	const next: QuestionDocument = { blocks, selection: collapsed(caret) };
	return { blocks, selection: collapsed(clamp(caret, next)) };
}

/**
 * Inserts a line break (Shift+Enter), with structural block behaviour:
 * - continuable block (list item / quote) that is empty: convert it to a
 *   paragraph — exits the list at the end, or splits it in the middle.
 * - list item / quote with content: split at the caret; the new line continues
 *   the block (ordered numbers increment).
 * - heading: split; the new line is a paragraph (headings are single-line).
 * - paragraph: split into two paragraphs.
 * The caret is computed by the op, never recovered from the rebuilt DOM.
 */
export function insertNewline(doc: QuestionDocument): QuestionDocument {
	const [from, to] = selectionBounds(doc.selection);
	const base = from === to ? doc : deleteRange(doc, from, to);
	const caret = from;
	const { block: bi, inner } = locate(base, caret);
	const block = base.blocks[bi];
	const blockStart = caret - inner;

	if (isContinuable(block.kind) && block.inlines.length === 0) {
		const blocks = [...base.blocks];
		blocks[bi] = { kind: PARAGRAPH, inlines: [] };
		return {
			blocks: renumberOrderedLists(blocks),
			selection: collapsed(blockStart),
		};
	}

	const { before, after } = splitInlines(block, inner);
	const first: Block = { kind: block.kind, inlines: normalizeInlines(before) };
	const second: Block = {
		kind: continuationKind(block.kind),
		inlines: normalizeInlines(after),
	};
	const blocks = [
		...base.blocks.slice(0, bi),
		first,
		second,
		...base.blocks.slice(bi + 1),
	];
	return {
		blocks: renumberOrderedLists(blocks),
		selection: collapsed(blockStart + inner + 1),
	};
}

/**
 * Input rule: if the collapsed caret sits immediately after a block marker typed
 * at the very start of a *paragraph* (e.g. the user just typed `- `, `## `,
 * `1. `), converts the block to that structural kind and removes the literal
 * marker text, leaving the caret at the start of the content. Returns null when
 * no marker rule applies, so the caller keeps the plain insert.
 */
export function applyBlockMarkerRule(
	doc: QuestionDocument,
): QuestionDocument | null {
	const { anchor, focus } = doc.selection;
	if (anchor !== focus) return null;
	const { block: bi, inner } = locate(doc, anchor);
	const block = doc.blocks[bi];
	if (block.kind.type !== "paragraph") return null;

	const first = block.inlines[0];
	if (first?.kind !== "text" || inner > first.value.length) return null;

	const before = first.value.slice(0, inner);
	const parsed = parseBlockMarker(before);
	if (parsed.kind.type === "paragraph" || parsed.rest.length !== 0) return null;

	const blockStart = anchor - inner;
	const inlines = normalizeInlines([
		{ kind: "text", value: first.value.slice(inner) },
		...block.inlines.slice(1),
	]);
	const blocks = [...doc.blocks];
	blocks[bi] = { kind: parsed.kind, inlines };
	return {
		blocks: renumberOrderedLists(blocks),
		selection: collapsed(blockStart),
	};
}

/**
 * Backspace-at-start rule: if the collapsed caret is at the very start of a
 * non-paragraph block, "outdents" it back to a paragraph (removing the bullet /
 * number / heading). Returns null otherwise so the caller does a normal delete.
 */
export function outdentBlock(doc: QuestionDocument): QuestionDocument | null {
	const { anchor, focus } = doc.selection;
	if (anchor !== focus) return null;
	const { block: bi, inner } = locate(doc, anchor);
	if (inner !== 0) return null;
	const block = doc.blocks[bi];
	if (block.kind.type === "paragraph") return null;
	const blocks = [...doc.blocks];
	blocks[bi] = { kind: PARAGRAPH, inlines: block.inlines };
	return { blocks: renumberOrderedLists(blocks), selection: doc.selection };
}

/** Replaces `[from, to]` with composed IME text (used on compositionend). */
export function applyComposition(
	doc: QuestionDocument,
	from: number,
	to: number,
	text: string,
): QuestionDocument {
	return spliceBlocks(doc, from, to, textToBlocks(text));
}

/** Sets the selection (offsets are clamped to the document). */
export function withSelection(
	doc: QuestionDocument,
	selection: DocSelection,
): QuestionDocument {
	return {
		blocks: doc.blocks,
		selection: {
			anchor: clamp(selection.anchor, doc),
			focus: clamp(selection.focus, doc),
		},
	};
}

// --- Autocomplete ---------------------------------------------------------

/**
 * The partial `@handle` immediately before a collapsed caret, as model offsets.
 * Only looks within the text run the caret sits in (atoms/line starts break the
 * run), mirroring the DOM `getMentionAtCaret`.
 *
 * `phrase` keeps spaces and punctuation inside the query. That is only for a
 * search the context selector is already showing: those characters anywhere
 * else end the mention, so typing on after dismissing the selector does not
 * bring it back.
 */
export function mentionAtCaret(
	doc: QuestionDocument,
	options?: { phrase?: boolean },
): { partial: string; start: number; end: number } | null {
	const { anchor, focus } = doc.selection;
	if (anchor !== focus) return null;
	const caret = anchor;
	const { block: bi, inner } = locate(doc, caret);
	const block = doc.blocks[bi];

	let acc = 0;
	let textBefore = "";
	for (const inline of block.inlines) {
		const len = inlineLength(inline);
		if (inner <= acc + len) {
			textBefore =
				inline.kind === "text" ? inline.value.slice(0, inner - acc) : "";
			break;
		}
		acc += len;
	}

	const match = textBefore.match(
		options?.phrase ? mentionPhraseAtCaretRegex : mentionAtCaretRegex,
	);
	if (!match) return null;
	return { partial: match[1], start: caret - match[0].length, end: caret };
}

/** An `@handle` written as plain text, located by model offsets. */
export interface HandleCandidate {
	/** The handle without its leading `@`. */
	handle: string;
	start: number;
	end: number;
}

/**
 * Every `@handle` written as plain text wholly inside `[from, to]`, in document
 * order. Candidates are read from within single text inlines, so an atom always
 * breaks a run, and a trailing `.` is excluded (sentence punctuation is far
 * likelier than a handle ending in a dot). The boundary rule mirrors
 * {@link mentionAtCaret}, so the `@` of an email address is not a candidate.
 */
export function handleCandidatesInRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): HandleCandidate[] {
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	const found: HandleCandidate[] = [];
	let acc = 0;
	for (const block of doc.blocks) {
		for (const inline of block.inlines) {
			if (inline.kind !== "text") {
				acc += 1;
				continue;
			}
			handleScanRegex.lastIndex = 0;
			for (
				let match = handleScanRegex.exec(inline.value);
				match !== null;
				match = handleScanRegex.exec(inline.value)
			) {
				const handle = match[1].replace(/\.+$/, "");
				const start = acc + match.index;
				const end = start + 1 + handle.length;
				if (handle.length > 0 && start >= lo && end <= hi) {
					found.push({ handle, start, end });
				}
			}
			acc += inline.value.length;
		}
		acc += 1; // the newline that follows the block
	}
	return found;
}

/** A video pill and the single offset it occupies. */
export interface VideoPillAt {
	offset: number;
	data: QuestionBoxVideoMentionData;
}

/**
 * Every video pill inside `[from, to]`, in document order. Used after a paste to
 * find the pills whose thumbnail needs fetching: an atom is one offset wide, so
 * the caller can swap a pill's data with `replaceRange(doc, offset, offset + 1,
 * …)` without moving anything around it.
 */
export function videoPillsInRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): VideoPillAt[] {
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	const found: VideoPillAt[] = [];
	let acc = 0;
	for (const block of doc.blocks) {
		for (const inline of block.inlines) {
			if (inline.kind === "pill" && acc >= lo && acc + 1 <= hi) {
				found.push({ offset: acc, data: inline.data });
			}
			acc += inlineLength(inline);
		}
		acc += 1; // the newline that follows the block
	}
	return found;
}

/** An account-mention chip and the single offset it occupies. */
export interface AccountMentionAt {
	offset: number;
	data: DocMentionData;
}

/**
 * Every account-mention chip inside `[from, to]`, in document order. The
 * counterpart of {@link videoPillsInRange}, and used the same way: to find the
 * chips whose avatar still needs resolving.
 */
export function accountMentionsInRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): AccountMentionAt[] {
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	const found: AccountMentionAt[] = [];
	let acc = 0;
	for (const block of doc.blocks) {
		for (const inline of block.inlines) {
			if (inline.kind === "mention" && acc >= lo && acc + 1 <= hi) {
				found.push({ offset: acc, data: inline.data });
			}
			acc += inlineLength(inline);
		}
		acc += 1; // the newline that follows the block
	}
	return found;
}

// --- Word-wise navigation -------------------------------------------------

/** What a single offset holds, as far as word-wise movement is concerned. */
type WordCell = "word" | "separator" | "atom";

/** Word constituents: letters, digits, `_`, and the apostrophe inside "don't". */
const WORD_CHAR_RE = /[\p{L}\p{N}_'\u2019]/u;

/**
 * One cell per addressable offset, so word-wise movement is a walk over runs.
 * The newline between blocks is a separator (word moves cross lines, as they do
 * natively), and an atom is its own kind so it never merges into a text run.
 */
function wordCells(doc: QuestionDocument): WordCell[] {
	const cells: WordCell[] = [];
	doc.blocks.forEach((block, index) => {
		if (index > 0) cells.push("separator");
		for (const inline of block.inlines) {
			if (inline.kind !== "text") {
				cells.push("atom");
				continue;
			}
			// Per UTF-16 code unit, matching the offset metric: both halves of a
			// surrogate pair get the same kind, so no boundary can split one.
			for (const unit of inline.value.split("")) {
				cells.push(WORD_CHAR_RE.test(unit) ? "word" : "separator");
			}
		}
	});
	return cells;
}

/**
 * Where a word-wise caret move from `offset` lands (Option-Arrow on a Mac),
 * counting each atom as a whole word: the move skips any separators, then
 * consumes exactly one word — a run of word characters, or a single atom, so a
 * run of adjacent pills is crossed one pill at a time. Returns `offset`
 * unchanged when there is no further word in `dir`.
 */
export function wordBoundary(
	doc: QuestionDocument,
	offset: number,
	dir: "left" | "right",
): number {
	const cells = wordCells(doc);
	let at = clamp(offset, doc);
	if (dir === "right") {
		while (at < cells.length && cells[at] === "separator") at++;
		if (at === cells.length) return at;
		if (cells[at] === "atom") return at + 1;
		while (at < cells.length && cells[at] === "word") at++;
		return at;
	}
	while (at > 0 && cells[at - 1] === "separator") at--;
	if (at === 0) return 0;
	if (cells[at - 1] === "atom") return at - 1;
	while (at > 0 && cells[at - 1] === "word") at--;
	return at;
}

/** Whether an atom (pill or mention chip) occupies any offset in `[from, to)`. */
export function hasAtomInRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): boolean {
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	return wordCells(doc)
		.slice(lo, hi)
		.some((cell) => cell === "atom");
}

// --- Construction & serialisation -----------------------------------------

/** The atom a mention segment stands for. */
const inlineFromSegment = (
	segment: Exclude<PromptSegment, { type: "text" }>,
): Inline =>
	segment.type === "video"
		? { kind: "pill", data: segment.value }
		: { kind: "mention", data: segment.value };

/**
 * The atom a decoded mention payload describes, or null when it describes no
 * mention we can render. Shared by every transport an atom arrives on — inline
 * JSON, and the mention links on the clipboard — so none of them has to know
 * how a payload maps onto the document model.
 */
export function inlineFromPayload(
	payload: Record<string, unknown>,
): Inline | null {
	const mention = mentionFromPayload(payload);
	return mention ? inlineFromSegment(mention) : null;
}

/** Splits pasted/source text into lines of inlines, turning mention JSON into atoms. */
function segmentsToLines(text: string): Inline[][] {
	const lines: Inline[][] = [[]];
	for (const segment of parsePromptSegments(text)) {
		if (segment.type !== "text") {
			lines[lines.length - 1].push(inlineFromSegment(segment));
			continue;
		}
		const parts = segment.value.split("\n");
		for (let i = 0; i < parts.length; i++) {
			if (i > 0) lines.push([]);
			if (parts[i].length > 0) {
				lines[lines.length - 1].push({ kind: "text", value: parts[i] });
			}
		}
	}
	return lines;
}

/** Pulls a leading block marker out of a line's inlines into structural metadata. */
function extractLeadingMarker(inlines: Inline[]): Block {
	const first = inlines[0];
	if (first?.kind !== "text") {
		return { kind: PARAGRAPH, inlines: normalizeInlines(inlines) };
	}
	const parsed = parseBlockMarker(first.value);
	if (parsed.kind.type === "paragraph") {
		return { kind: PARAGRAPH, inlines: normalizeInlines(inlines) };
	}
	return {
		kind: parsed.kind,
		inlines: normalizeInlines([
			{ kind: "text", value: parsed.rest },
			...inlines.slice(1),
		]),
	};
}

/** Builds a document from a raw source string, caret placed at the end. */
export function docFromSource(source: string): QuestionDocument {
	const parsed = segmentsToLines(source).map(extractLeadingMarker);
	const blocks = renumberOrderedLists(parsed);
	if (blocks.length === 0) blocks.push({ kind: PARAGRAPH, inlines: [] });
	const doc: QuestionDocument = { blocks, selection: collapsed(0) };
	return { blocks, selection: collapsed(docLength(doc)) };
}

/** An inline as human-readable text: mentions as `@handle`, pills as their label. */
export const inlineSource = (inline: Inline): string => {
	if (inline.kind === "text") return inline.value;
	if (inline.kind === "mention") return `@${inline.data.handle}`;
	return inline.data.label;
};

/**
 * An inline as inline JSON: the wire format shared by the submitted prompt and
 * the clipboard, so an atom survives both round trips (see
 * promptMentionSerialization).
 */
export const inlineAugmented = (inline: Inline): string => {
	if (inline.kind === "text") return inline.value;
	if (inline.kind === "mention") return serializeAccountMention(inline.data);
	return serializeVideoMention(inline.data);
};

/** Blocks as text: each block's marker followed by its serialised content. */
const serializeBlocks = (
	blocks: Block[],
	inline: (inline: Inline) => string,
): string =>
	blocks
		.map((block) => markerFor(block.kind) + block.inlines.map(inline).join(""))
		.join("\n");

/**
 * Raw text as read back for the `question` signal and mention detection: the
 * block marker plus content, with atoms in their human-readable form.
 */
export function toSourceText(doc: QuestionDocument): string {
	return serializeBlocks(doc.blocks, inlineSource);
}

/**
 * The submitted prompt: block marker plus content (atoms as their mention JSON,
 * blocks joined by newlines).
 */
export function toAugmentedText(doc: QuestionDocument): string {
	return serializeBlocks(doc.blocks, inlineAugmented);
}

/**
 * The content of the selection `[from, to]` as blocks, for the clipboard.
 *
 * A block keeps its kind only when the selection reaches its start: markers are
 * structural rather than content, so copying half of a list item yields a
 * paragraph, which is what re-pasting it should produce. Blocks outside the
 * range are dropped and the first/last are sliced to it.
 */
export function blocksForRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): Block[] {
	const lo = Math.min(from, to);
	const hi = Math.max(from, to);
	const selected: Block[] = [];
	let acc = 0;
	for (const block of doc.blocks) {
		const blockStart = acc;
		const blockEnd = acc + blockLength(block);
		acc = blockEnd + 1;
		if (blockStart > hi || blockEnd < lo) continue;
		const sliceLo = Math.max(lo, blockStart) - blockStart;
		const sliceHi = Math.min(hi, blockEnd) - blockStart;
		const upTo: Block = {
			kind: block.kind,
			inlines: splitInlines(block, sliceHi).before,
		};
		selected.push({
			kind: lo <= blockStart ? block.kind : PARAGRAPH,
			inlines: splitInlines(upTo, sliceLo).after,
		});
	}
	return selected;
}

/**
 * The selection as prompt text — atoms as their mention JSON — used for the
 * augmented clipboard flavour and the debug copy.
 */
export function sourceTextForRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): string {
	return serializeBlocks(blocksForRange(doc, from, to), inlineAugmented);
}

/**
 * The selection as human-readable text (`@handle`, video labels), used for the
 * clipboard's `text/plain` flavour so a paste into another app reads as prose.
 */
export function plainTextForRange(
	doc: QuestionDocument,
	from: number,
	to: number,
): string {
	return serializeBlocks(blocksForRange(doc, from, to), inlineSource);
}

/** True when the document has no content text and no atoms in any block. */
export function isEmptyDocument(doc: QuestionDocument): boolean {
	return doc.blocks.every((block) => block.inlines.length === 0);
}
